import { useTracking } from '@repo/tracking/use-tracking';
import { useTranslation } from '@repo/translations/use-translation';
import { useState } from 'react';

import { ButtonAnchor, PrimaryButton, SecondaryButton } from '@zooplus/ui-components';

import { useAddMembershipToCart } from '../../../../hooks/useCart';
import { scrollToElement } from '../../../../hooks/useScrollTo';
import { PROGRAM_PLANS_ANCHOR_ID } from '../../program-plan/ProgramPlan/ProgramPlan';

import styles from './BannerButtons.module.css';

export interface BannerButtonsProps {
  isCustomerLoggedIn: boolean;
  isBasicCustomer: boolean;
  hasOfferAvailable: boolean;
  articleId?: number;
  setIsModalOpen: (value: boolean) => void;
}

export const BannerButtons = ({
  isCustomerLoggedIn,
  isBasicCustomer,
  hasOfferAvailable,
  articleId,
  setIsModalOpen,
}: BannerButtonsProps) => {
  const [isAddingMembership, setIsAddingMembership] = useState(false);
  const { addMembershipToCart } = useAddMembershipToCart();
  const { trackClick } = useTracking();
  const { t } = useTranslation();

  if (isCustomerLoggedIn) {
    if (isBasicCustomer && hasOfferAvailable) {
      return (
        <div className={styles.buttonsContainer}>
          <PrimaryButton
            data-testid="join-button"
            length={innerWidth < 768 ? 'full' : undefined}
            className={styles.joinButton}
            isLoading={isAddingMembership}
            onClick={async () => {
              trackClick({
                target: {
                  name: 'landing_page_banner',
                  id: 'join_logged',
                },
              });

              setIsAddingMembership(true);
              try {
                await addMembershipToCart(articleId);
                setIsModalOpen(true);
              } finally {
                setIsAddingMembership(false);
              }
            }}>
            {t('loyalty-program.public-landing-page.banner.join-button.basic')}
          </PrimaryButton>
          <SecondaryButton
            data-testid="benefits-button"
            className={styles.benefitsButton}
            length={innerWidth < 768 ? 'full' : undefined}
            onClick={() => {
              scrollToElement(document.getElementById(PROGRAM_PLANS_ANCHOR_ID));
              trackClick({
                target: {
                  name: 'landing_page_banner',
                  id: 'compare_plans_logged',
                },
              });
            }}>
            {t('loyalty-program.public-landing-page.banner.benefits-button')}
          </SecondaryButton>
        </div>
      );
    } else if (!isBasicCustomer && hasOfferAvailable) {
      return (
        <PrimaryButton
          data-testid="join-button"
          length={innerWidth < 768 ? 'full' : undefined}
          className={styles.joinButton}
          isLoading={isAddingMembership}
          onClick={async () => {
            trackClick({
              target: {
                name: 'landing_page_banner',
                id: 'join_logged',
              },
            });

            setIsAddingMembership(true);
            try {
              await addMembershipToCart(articleId);
              setIsModalOpen(true);
            } finally {
              setIsAddingMembership(false);
            }
          }}>
          {t('loyalty-program.public-landing-page.banner.join-button.premium')}
        </PrimaryButton>
      );
    } else {
      return null;
    }
  } else {
    return (
      <div className={styles.buttonsContainer}>
        <ButtonAnchor
          onClick={() => {
            trackClick({
              target: {
                name: 'landing_page_banner',
                id: 'sign_in_not_logged',
              },
            });
          }}
          href="/account/zooclub/dashboard"
          data-testid="sign-in-button"
          length={innerWidth < 768 ? 'full' : undefined}
          className={styles.joinButton}>
          {t('loyalty-program.public-landing-page.banner.join-button.not-logged')}
        </ButtonAnchor>
        <SecondaryButton
          data-testid="benefits-button"
          className={styles.benefitsButton}
          length={innerWidth < 768 ? 'full' : undefined}
          onClick={() => {
            scrollToElement(document.getElementById(PROGRAM_PLANS_ANCHOR_ID));
            trackClick({
              target: {
                name: 'landing_page_banner',
                id: 'compare_plans_logged',
              },
            });
          }}>
          {t('loyalty-program.public-landing-page.banner.benefits-button')}
        </SecondaryButton>
      </div>
    );
  }
};
